import React from "react";
import PropTypes from 'prop-types';
import { useTranslation } from "react-i18next";
import * as echarts from "echarts";
import _ from 'lodash';
import { roRO } from "@mui/material/locale";


const valueInitialize = (jsonData, t) => {
    const configs = [
        { key: 'fraIns', name: 'fragrance' },
        { key: 'aromaIns', name: 'aroma' },
        { key: 'flaIns', name: 'flavor' },
        { key: 'aftIns', name: 'aftertaste' },
        { key: 'acdIns', name: 'acidity' },
        { key: 'swtIns', name: 'sweetness' },
        { key: 'mfIns', name: 'mouthfeel' },
    ];

    const calcValue = (obj) => {
        if (!obj) return 0;
        return (obj.low ? (obj.low/100) * 100 : 0) + (obj.medium ? (obj.medium/100) * 200 : 0) + (obj.high ? (obj.high/100) * 300 : 0);
    }

    const getValueList = (obj) => {
        return [
            { name: t('assessment.low'), value: obj?.low ?? 0 },
            { name: t('assessment.medium'), value: obj?.medium ?? 0 },
            { name: t('assessment.high'), value: obj?.high ?? 0 }
        ];
    }

    const getMaxObj = (obj) => {
        return getValueList(obj).reduce( (prev, current) => {
            return prev.value >= current.value ? prev : current;
        });
    }

    const makeText = (name) => {
        let text = t(`assessment.${name}`);
        return text;
    }

    try {
        return configs.map(cfg => ({
            id: cfg.name,
            name: makeText(cfg.name),
            text: cfg.name.charAt(0).toUpperCase() + cfg.name.slice(1), 
            max: 300,
            value: calcValue(jsonData[cfg.key]),
            valueText: getMaxObj(jsonData[cfg.key]).name,
            valueList: getValueList(jsonData[cfg.key]),
        }));
    }
    catch(err) {
        console.error("Error in valueInitialize:", err);
        return [];
    }
};

export const ChartRadar = (props) => {
    const { jsonData, chartName, chartHeight } = props;
    const { t } = useTranslation();
    const chartRef = React.useRef(null);
    const myChart = React.useRef(null);

    React.useEffect(() => {
        if (!myChart.current && chartRef.current) {
            myChart.current = echarts.init(chartRef.current);
        }

        const indicatorList = jsonData ? valueInitialize(jsonData, t) : {};
        // console.log("indicatorList", indicatorList, jsonData);

        const tooltipFormatter = (itemList) => {
            return itemList.map(item => {
                if (item.valueText === t('assessment.low')) {
                    return ` <span>${item.name}</span> : <span>${t('assessment.low')}</span><br />`;
                } else if (item.valueText === t('assessment.high')) {
                    return ` <span style="font-weight: bold;">${item.name}</span> : <span style="color: red; font-weight: bold">${t('assessment.high')}</span><br />`;
                } else {
                    return ` <span style="font-weight: 510">${item.name}</span> : <span style="color: blue; font-weight: 510">${t('assessment.medium')}</span><br />`;
                }
                return '';
            }).join(' ');
        };

        const option = {
            title: {
                text: chartName,
                textStyle: {
                    fontSize: 30,
                    fontStyle: "normal",
                    color: "#131213",
                    fontWeight: "bold"
                },
                show: false,
            },
            tooltip: {
                show: true,
                trigger: 'axis',
                showDelay: 0,
                hideDelay: 100,
                transitionDuration: 0.4,
                enterable: false,
                backgroundColor: '#fff',
                shadowBlur: 10,
                shadowColor: "rgba(0, 0, 0, .2)",
                formatter: function (params, ticket) {
                    return tooltipFormatter(indicatorList);
                }
            },
            legend: {
                left: 'center',
                data: [
                    chartName
                ],
                show: false,
            },
            textStyle: {
                fontSize: Math.round(myChart.current._dom.offsetWidth) < 500 ? 11 : 13,
                fontStyle: "normal",
                color: "#464646",
                fontWeight: "normal"
            },
            radar: [{
                indicator: indicatorList,
                center: ['50%', '50%'],
                radius: Math.round(myChart.current._dom.offsetWidth) < 400 ? 80 : Math.round(myChart.current._dom.offsetWidth) < 500 ? 100 : 140,
                axisName : { show: true},
            }],
            series: [{
                type: 'radar',
                tooltip: {
                    trigger: 'item'
                },
                areaStyle: {},
                data: [{
                    value: indicatorList?.map(obj => obj.value) || [],
                    name: chartName,
                }],
            }]
        };

        if (myChart.current) {
            myChart.current.showLoading();
            myChart.current.setOption(option);
            myChart.current.hideLoading();
        }
        
        
        // 창크기 변경할때 카트를 갱신한다.
        const handleResize = _.debounce(() => {
            if (myChart.current) {
                myChart.current.showLoading();
                myChart.current.setOption(option);
                myChart.current?.resize();
                myChart.current.hideLoading();
            }
        }, 50); // Debounce for 50ms

        window.addEventListener('resize', handleResize);

        return () => {
            try {
                window.removeEventListener('resize', handleResize);
            
                myChart.current && myChart.current.dispose();
                myChart.current && myChart.current.dispose();
                myChart.current = null;
            }
            catch (err) {
                console.log("Unload", new Date().toISOString(), JSON.stringify(err), err);
            }
        };
    }, [jsonData, chartName, chartHeight]);


    return <div ref={chartRef} style={{ width: "100%", height: chartHeight || "200px" }} />;
}

ChartRadar.propTypes = {
    jsonData: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.func,
        PropTypes.object,
    ]).isRequired,
    chartName: PropTypes.string,
    chartHeight: PropTypes.string,
};
